import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ProductBuyBarRefs
 * @property {HTMLButtonElement} addToCartButton - The bar's add-to-cart button.
 * @property {HTMLElement} [error] - The bar's error message element.
 * @property {HTMLElement} [liveRegion] - The bar's screen-reader announcement region.
 */

/**
 * Permanent mobile buy bar.
 *
 * Fixed to the bottom of the viewport on mWeb (<=749px) and the sole add-to-cart on
 * mobile: it proxies clicks to the in-flow product form's add-to-cart button (which is
 * hidden on mobile via CSS), mirrors that button's availability/label, shows a loading
 * spinner while the request is in flight, and surfaces add-to-cart errors. On desktop the
 * bar is hidden via CSS — the sticky in-flow buy column is the add-to-cart there.
 *
 * The action row is built to hold more than one CTA (e.g. a future "Buy now" beside the
 * add-to-cart); the add-to-cart flexes to fill whatever space is left.
 *
 * @extends {Component<ProductBuyBarRefs>}
 */
class ProductBuyBar extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number | undefined} */
  #errorTimeout;

  /** @type {number | undefined} */
  #announceTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#cacheTargetButton();
    this.#syncFromTarget();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#errorTimeout);
    clearTimeout(this.#announceTimeout);
  }

  /**
   * Proxies the bar's add-to-cart to the in-flow product form button.
   */
  handleAddToCartClick = () => {
    if (this.#isNotifyActive()) {
      // Notify state: hand off to the in-flow Notify CTA (a [data-notify-trigger]); the inline
      // toggle in snippets/notify-dialog.liquid injects the module and opens the shared dialog.
      // No add-to-cart proxy, no loading spinner.
      this.#getNotifyTrigger()?.click();
      return;
    }

    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled) return;

    const { addToCartButton } = this.refs;
    addToCartButton.dataset.loading = 'true';
    addToCartButton.setAttribute('aria-busy', 'true');

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;
    // The product form morphs the in-flow button on this same event; re-sync on the next
    // frame so we read its updated availability/label regardless of listener order.
    requestAnimationFrame(() => {
      this.#cacheTargetButton();
      this.#syncFromTarget();
    });
  };

  /**
   * Success (or non-error completion) — reset the in-flight state and clear any error.
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    this.#resetInFlight();
    if (event.detail?.data?.didError) return; // the matching cart:error handles the message
    this.#hideError();
    // The in-flow form's live region is hidden on mobile, so announce success here.
    // Only audible on mobile — the bar is display:none on desktop.
    this.#announce(window.Theme?.translations?.added || 'Added to cart');
  };

  /**
   * Error — reset the in-flight state and surface the message on the bar.
   * @param {CustomEvent} event
   */
  #handleCartError = (event) => {
    this.#resetInFlight();
    const message = event.detail?.data?.message;
    const { error } = this.refs;
    if (!error || !message) return;
    error.textContent = message;
    error.hidden = false;
    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#hideError(), 10000);
  };

  #hideError() {
    const { error } = this.refs;
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
  }

  /**
   * Announces a message to screen readers via the bar's live region.
   * @param {string} message
   */
  #announce(message) {
    const { liveRegion } = this.refs;
    if (!liveRegion) return;
    liveRegion.textContent = message;
    clearTimeout(this.#announceTimeout);
    this.#announceTimeout = setTimeout(() => {
      liveRegion.textContent = '';
    }, 5000);
  }

  /**
   * Clears the puppet flag and the bar button's loading spinner.
   */
  #resetInFlight() {
    if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
    const { addToCartButton } = this.refs;
    delete addToCartButton.dataset.loading;
    addToCartButton.removeAttribute('aria-busy');
  }

  /**
   * Mirrors the in-flow add-to-cart button's availability and label onto the bar button,
   * so sold-out / unavailable states stay in sync without duplicating the Liquid logic.
   */
  #syncFromTarget() {
    const { addToCartButton } = this.refs;
    const barLabel = addToCartButton.querySelector('.add-to-cart-text__content');

    // Notify state: mirror the in-flow Notify CTA's label and stay enabled (it opens the dialog,
    // it never adds to cart). data-notify-active drives the cart→bell glyph swap in CSS.
    if (this.#isNotifyActive()) {
      addToCartButton.disabled = false;
      this.dataset.variantAvailable = 'false';
      this.dataset.notifyActive = 'true';
      const label = this.#getNotifyTrigger()?.querySelector('.add-to-cart-text__content')?.textContent?.trim();
      if (barLabel && label) barLabel.textContent = label;
      return;
    }

    this.dataset.notifyActive = 'false';
    const target = this.#targetAddToCartButton;
    if (!target) return;

    addToCartButton.disabled = target.disabled;
    this.dataset.variantAvailable = String(!target.disabled);

    const targetLabel = target.querySelector('.add-to-cart-text__content');
    const text = targetLabel?.textContent?.trim();
    if (barLabel && text) barLabel.textContent = text;
  }

  /** @returns {boolean} Whether the in-flow buy-buttons block is currently in its Notify state. */
  #isNotifyActive() {
    return this.#getBuyButtonsBlock()?.classList.contains('is-notify') ?? false;
  }

  /** @returns {Element | null} */
  #getBuyButtonsBlock() {
    return this.closest('.shopify-section')?.querySelector('.buy-buttons-block[data-notify]') ?? null;
  }

  /** @returns {HTMLElement | null} */
  #getNotifyTrigger() {
    return /** @type {HTMLElement | null} */ (
      this.#getBuyButtonsBlock()?.querySelector('[data-notify-trigger]') ?? null
    );
  }

  #cacheTargetButton() {
    const form = this.#getProductForm();
    this.#targetAddToCartButton = /** @type {HTMLButtonElement | null} */ (
      form?.querySelector('[ref="addToCartButton"]') ?? null
    );
  }

  /**
   * @returns {Element | null}
   */
  #getProductForm() {
    const section = this.closest('.shopify-section');
    if (!section) return null;
    return section.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }
}

if (!customElements.get('product-buy-bar')) {
  customElements.define('product-buy-bar', ProductBuyBar);
}
